import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { journalsApi } from '../../api/endpoints';
import DataTable from '../../components/DataTable';
import useAdminList from '../../hooks/useAdminList';
import { useUi } from '../../context/UiContext';
import styles from '../listPage.module.css';

const JournalsList = () => {
    const { toast, confirm } = useUi();
    const list = useAdminList(journalsApi.list, { sort: 'name', dir: 'asc' });
    const { reload } = list;

    const [newName, setNewName] = useState('');
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState('');
    const [editing, setEditing] = useState(null);

    const handleCreate = async (e) => {
        e.preventDefault();
        const name = newName.trim();
        if (!name) {
            setCreateError('Введите название журнала.');
            return;
        }

        setCreating(true);
        setCreateError('');
        try {
            await journalsApi.create({ name });
            toast.success('Журнал добавлен');
            setNewName('');
            reload();
        } catch (err) {
            setCreateError(err.fieldError('name') || err.message);
        } finally {
            setCreating(false);
        }
    };

    const handleSaveEdit = async () => {
        const name = editing.name.trim();
        if (!name) return;

        try {
            await journalsApi.update(editing.id, { name });
            toast.success('Сохранено');
            setEditing(null);
            reload();
        } catch (err) {
            toast.error(err.message);
        }
    };

    const handleDelete = async (row) => {
        const ok = await confirm({
            title: 'Удалить журнал?',
            message:
                row.releases > 0
                    ? `«${row.name}»\n\nУ журнала ${row.releases} выпуск(ов). Они останутся в базе, но потеряют привязку к журналу.`
                    : `«${row.name}»\n\nВыпусков у журнала нет.`,
            confirmLabel: 'Удалить',
            danger: true,
        });
        if (!ok) return;

        try {
            await journalsApi.remove(row.id, { force: row.releases > 0 });
            toast.success('Журнал удалён');
            reload();
        } catch (err) {
            toast.error(err.message);
        }
    };

    const columns = [
        { key: 'id', title: 'ID', width: 80, sortable: true },
        {
            key: 'name',
            title: 'Название',
            sortable: true,
            render: (row) =>
                editing && editing.id === row.id ? (
                    <div className={styles.editRow}>
                        <input
                            className="adm-input"
                            value={editing.name}
                            autoFocus
                            onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSaveEdit();
                                if (e.key === 'Escape') setEditing(null);
                            }}
                        />
                        <button type="button" className="adm-btn adm-btn-primary adm-btn-sm" onClick={handleSaveEdit}>
                            Сохранить
                        </button>
                        <button
                            type="button"
                            className="adm-btn adm-btn-ghost adm-btn-sm"
                            onClick={() => setEditing(null)}
                        >
                            Отмена
                        </button>
                    </div>
                ) : (
                    row.name
                ),
        },
        {
            key: 'releases',
            title: 'Выпусков',
            width: 150,
            render: (row) =>
                row.releases > 0 ? (
                    <NavLink to={`/admin/releases?journal=${row.id}`} className={styles.usageBadge}>
                        {row.releases} →
                    </NavLink>
                ) : (
                    <span className="adm-muted">—</span>
                ),
        },
        {
            key: 'actions',
            title: '',
            width: 150,
            align: 'right',
            render: (row) => (
                <div className={styles.rowActions}>
                    <button
                        type="button"
                        className="adm-btn adm-btn-ghost adm-btn-sm"
                        onClick={() => setEditing({ id: row.id, name: row.name })}
                    >
                        Изменить
                    </button>
                    <button
                        type="button"
                        className="adm-btn adm-btn-danger adm-btn-sm"
                        onClick={() => handleDelete(row)}
                    >
                        Удалить
                    </button>
                </div>
            ),
        },
    ];

    return (
        <>
            <div className="adm-page-header">
                <div>
                    <h1 className="adm-page-title">Журналы</h1>
                    <p className="adm-page-subtitle">Издания, к которым относятся выпуски и статьи</p>
                </div>
                <NavLink to="/admin/releases" className="adm-btn adm-btn-secondary">
                    Выпуски →
                </NavLink>
            </div>

            <div className="adm-card" style={{ marginBottom: 20 }}>
                <div className="adm-card-header">
                    <h2 className="adm-card-title">Добавить журнал</h2>
                </div>
                <div className="adm-card-body">
                    <form className={styles.inlineForm} onSubmit={handleCreate}>
                        <div className={styles.inlineFormInput}>
                            <input
                                className={`adm-input ${createError ? 'adm-input-invalid' : ''}`}
                                value={newName}
                                onChange={(e) => {
                                    setNewName(e.target.value);
                                    setCreateError('');
                                }}
                                placeholder="Название журнала"
                                aria-label="Название журнала"
                            />
                            {createError && <div className="adm-error">{createError}</div>}
                        </div>
                        <button type="submit" className="adm-btn adm-btn-primary" disabled={creating}>
                            {creating ? 'Добавление…' : 'Добавить'}
                        </button>
                    </form>
                </div>
            </div>

            <DataTable
                columns={columns}
                rows={list.rows}
                loading={list.loading}
                error={list.error}
                onRetry={list.reload}
                search={list.search}
                onSearchChange={list.setSearch}
                searchPlaceholder="Поиск по названию"
                sort={list.sort}
                dir={list.dir}
                onSortChange={list.toggleSort}
                page={list.page}
                perPage={list.perPage}
                total={list.total}
                lastPage={list.lastPage}
                onPageChange={list.setPage}
                onPerPageChange={list.setPerPage}
                emptyText={list.search ? 'По запросу ничего не найдено' : 'Журналов пока нет'}
            />
        </>
    );
};

export default JournalsList;
